<template>
  <CustomModal
    class="confirm-smart-boost-modal"
    :is-modal-open="isConfirmBoostModalOpen"
    @toggleModal="closeModal"
    @onClickAway="closeModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[636px] tw:md:w-[636px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader
      ><p class="tw:text-xl tw:font-bold">{{ $t('rc_smartboost_confirm_header') }}</p></template
    >

    <div class="tw:flex tw:flex-col tw:w-full tw:gap-y-6">
      <p>{{ $t('rc_smartboost_confirm_info') }}</p>
      <div class="tw:flex tw:flex-col tw:w-full tw:mb-">
        <div class="tw:flex tw:w-full tw:justify-between tw:mb-2">
          <div class="tw:w-3/4 tw:font-bold">{{ $t('rc_smartboost_overview_dates') }}</div>
          <div class="tw:w-1/4 tw:font-bold tw:flex tw:justify-end">{{ $t('rc_smartboost_overview_commission') }}</div>
        </div>
        <div class="tw:h-[1px] tw:w-full tw:bg-gray-300 tw:mb-3" />
        <div class="tw:flex tw:gap-x-8 tw:w-full tw:justify-between tw:mb-2">
          <div class="tw:flex-wrap tw:text-sm tw:text-black tw:flex tw:gap-2">
            <span v-for="(date, index) in listOfDates" :key="index" class="tw:bg-gray-200 tw:p-1 tw:rounded">{{
              date
            }}</span>
          </div>
          <div class="tw:w-1/4 tw:text-sm tw:font-bold tw:flex tw:justify-end">
            {{ formatPercentage(language, selectedCommission, 0, 0) }}
          </div>
        </div>
      </div>
      <div class="tw:text-xs tw:leading-[18px]">
        {{ $t('rc_smartboost_overview_footnote') }}
      </div>
    </div>

    <template #modalFooter>
      <div class="tw:flex tw:w-full tw:items-start tw:md:items-center tw:md:flex-row tw:flex-col">
        <div class="tw:flex tw:w-full tw:justify-between tw:md:justify-end tw:gap-x-4 tw:mt-4 tw:md:mt-0">
          <GhostButton
            class="tw:w-full tw:md:w-auto"
            :dataTestId="'smart-boost-confirm-modal-back-button'"
            @click="toggleConfirmBoostModal('close')"
          >
            {{ $t('rc_smartboost_back_cta') }}
          </GhostButton>
          <MainButton
            class="tw:w-full tw:md:w-auto"
            :dataTestId="'smart-boost-confirm-modal-confirm-button'"
            @click="confirmBoost"
            :disabled="isLoading"
          >
            <span v-show="!isLoading" class="tw:text-nowrap">{{ $t('rc_smartboost_confirm_cta') }}</span>
            <span v-show="isLoading"> <SmallDotLoader color="#fff" /></span>
          </MainButton>
        </div>
      </div>
    </template>
  </CustomModal>
</template>
<script setup>
import { ref, computed } from 'vue'

import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import CustomModal from '@/components/base/CustomModal.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'

import { useSmartBoost } from './useSmartBoost'
import { useAddBoostMutation } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { formatPercentage } from '@/utils/Utility.ts'

import useToastNotifications from '@/components/notifications/useToastNotifications'

import appAnalytics from '@/utils/tracking'
import { useSessionValues } from '@/composables/useSessionValues'

const isLoading = ref(false)
const { language } = useSessionValues()
const { displayNotification, showSaveError } = useToastNotifications()
const { addBoost } = useAddBoostMutation()
const {
  toggleConfirmBoostModal,
  isConfirmBoostModalOpen,
  selectedCommission,
  getSelectedDatesFormatted,
  switchToEditingMode,
  setActiveBoostDates,
} = useSmartBoost()

const listOfDates = computed(() => getSelectedDatesFormatted.value.map(item => item.formattedDate))
const intervals = computed(() =>
  getSelectedDatesFormatted.value.map(item => {
    const startDate = new Date(item.timestamps[0])
    const endTimestamp = item.isRange ? item.timestamps[item.timestamps.length - 1] : item.timestamps[0]
    const endDate = new Date(endTimestamp)

    return {
      startDate: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
      endDate: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`,
    }
  })
)

/** ISO 8601 strings for Segment — same millisecond ranges as {@link intervals} (API body stays date-only). */
const boostIntervalsForAnalytics = computed(() =>
  getSelectedDatesFormatted.value.map(item => {
    const startTimestamp = item.timestamps[0]
    const endTimestamp = item.isRange ? item.timestamps[item.timestamps.length - 1] : item.timestamps[0]
    return {
      startDate: new Date(startTimestamp).toISOString(),
      endDate: new Date(endTimestamp).toISOString(),
    }
  })
)

const closeModal = () => {
  toggleConfirmBoostModal('close')
}

const confirmBoost = async () => {
  if (!selectedAccommodationId.value) return
  isLoading.value = true

  try {
    await addBoost({
      accommodationId: selectedAccommodationId.value,
      body: {
        intervals: intervals.value,
        boostCommissionLevel: selectedCommission.value,
      },
    })

    appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_NEW_BOOST_CREATED, {
      item_id: selectedAccommodationId.value,
      boost_intervals: boostIntervalsForAnalytics.value,
      boost_commission_level: selectedCommission.value,
    })

    isLoading.value = false
    closeModal()

    const allTimestamps = getSelectedDatesFormatted.value.flatMap(item => item.timestamps)
    setActiveBoostDates(allTimestamps)
    switchToEditingMode()

    displayNotification({
      title: 'setting_updated_success',
      titleTranslationKey: true,
      message: 'rc_smartboost_success',
      isTranslationKey: true,
      type: 'success',
    })
  } catch {
    isLoading.value = false
    showSaveError()
  }
}
</script>
